import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FieldInputProps } from 'react-final-form';

import { colorsByStatus, titleByRuleType } from '@front/common/src/forms/validators/password/utils';
import { useValidatedPassword } from '@front/common/src/forms/validators/password/use-validated-password';
import {
  PasswordRuleProps,
  PasswordRuleType,
  PasswordStatus,
} from '@front/common/src/forms/validators/password/type';

import { translate } from '@front/i18n';

import Title from '../base-text/variants/title';
import Text from '../base-text/variants/text';
import Icon from '../icon/icon';
import { minWidth } from '../../styles/mixins';

import InputPasswordLegacy, { InputProps } from './input-password-legacy';

const t = translate('components.validated_password');

const StyledRule = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    text-align: center;
  }
`;

const StyledIcon = styled(Icon)`
  margin-bottom: 2.5px;
`;

const RulesContainer = styled.div`
  margin-top: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  ${minWidth.md`
    gap: 10px;
  `};
`;

const RulesBlock = styled.div`
  gap: 7px;
  margin: -10px 0 15px 15px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Rule = ({ status = 'stale', ruleType }: PasswordRuleProps) => (
  <StyledRule>
    <div>
      {status === 'valid' ? (
        <StyledIcon dataTestId={`checkmark-${ruleType}`} name='check-circle' size={15} color={colorsByStatus.valid} />
      ) : (
        <Title.SM color={colorsByStatus[status]}>{titleByRuleType[ruleType]}</Title.SM>
      )}
    </div>
    <Text.XSMedium color={colorsByStatus[status]}>{t(`.${ruleType}`)}</Text.XSMedium>
  </StyledRule>
);

export type ValidatedPasswordPropsLegacy = FieldInputProps<HTMLInputElement> &
  InputProps & {
    onError?: () => void;
    enterKeyHint?: 'next' | 'done' | 'go' | 'search' | 'send';
  };

export const ValidatedPasswordLegacy = React.forwardRef<HTMLInputElement, ValidatedPasswordPropsLegacy>(
  ({ input, meta, onError, enterKeyHint = 'done', ...rest }, ref) => {
    const {
      state,
      invalidateState,
      onChange,
      onFocus,
      isFocused,
      setBlurred,
      isRulesVisible,
      areAllRequirementsMet,
    } = useValidatedPassword(input.value);

    const handleOnError = useCallback(
      (newState: Record<PasswordRuleType, PasswordStatus>) => {
        Object.entries(newState).forEach(([rule, value]) => {
          if (value === 'invalid') {
            onError?.(rule);
          }
        });
      },
      [onError],
    );

    useEffect(() => {
      if (meta.touched && meta.error && !isFocused) {
        invalidateState(handleOnError);
      }
    }, [meta.error, meta.touched, invalidateState, isFocused, handleOnError]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      input.onChange?.(e.target.value);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus();
      input.onFocus?.(e);
    };

    const handleBlur = () => {
      setTimeout(() => {
        setBlurred();
        invalidateState(handleOnError);
      }, 200);
    };
    const displayRules = !!(isRulesVisible || (meta.touched && meta.error));
    return (
      <>
        <InputContainer>
          <InputPasswordLegacy
            enterKeyHint={enterKeyHint}
            input={input}
            meta={meta}
            {...rest}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={ref}
            isErrorDisplay={false}
            isBorderErrorWithoutErrorDisplay={displayRules && !areAllRequirementsMet}
          />
        </InputContainer>
        {displayRules && (
          <RulesBlock data-testid='rulesBolck'>
            <Text.XSMedium color={colorsByStatus.stale}>{t('.text')}</Text.XSMedium>
            <RulesContainer>
              <Rule status={state.upperCase} ruleType='upperCase' />
              <Rule status={state.lowerCase} ruleType='lowerCase' />
              <Rule status={state.number} ruleType='number' />
              <Rule status={state.symbol} ruleType='symbol' />
              <Rule status={state.length} ruleType='length' />
            </RulesContainer>
          </RulesBlock>
        )}
      </>
    );
  },
);
